/**
 * SE2 · Product gallery (2026-11-04). Markup snippets/se2-gallery.liquid, styles assets/se2-gallery.css.
 *
 * Drives the stage the product pages draw from 990 (the Slider board): one photo shown, the others stacked under it,
 * a cross-fade between them, arrows, thumbnails, the "01 / 04" counter and a click on the photo for the next one.
 * Below 990 the stage is hidden and Horizon's own slideshow does the work, so nothing here runs a timer or listens to
 * scroll on a phone; the listeners are clicks, keys and two events.
 *
 * What Horizon's gallery used to give for free, rewired:
 *   - VARIANT MEDIA. A size or colour change (shopify:product:select on the section) brings that variant's photo
 *     forward, in the product's order, so the thumbnails never reshuffle. A variant with no photo goes to photo 1.
 *     When the photos themselves change (hide_variants, another colour), the stage is taken from the fetched HTML.
 *     A late answer for a size that is no longer chosen is dropped (the sequence number).
 *   - ZOOM. The zoom button opens Horizon's zoom dialog (it stays inside the gallery, so the page has one dialog) on
 *     the photo in view; the dialog's own order is Horizon's, so the two are matched by media id, never by position.
 *     Scrolling inside the dialog (zoom-media:selected) moves the stage with it, so closing lands on the same photo.
 *
 * Only the photo in view loads with the page. Its neighbours load when the stage first moves or is hovered, and the
 * rest once the page is idle.
 *
 * Events: se2:gallery-change (bubbles) with { index, mediaId, count } after every move.
 * Methods: select(index), selectMedia(mediaId), next(), prev().
 */

(() => {
  if (customElements.get('se2-gallery')) return;

  const idle = (fn) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2500 });
    else window.setTimeout(fn, 800);
  };

  const pad = (n) => String(n).padStart(2, '0');

  class Se2Gallery extends HTMLElement {
    /** @type {AbortController | undefined} */
    #abort;
    #index = 0;
    #seq = 0;
    #zoomOpen = false;

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      this.#index = Number(this.dataset.active) || 0;
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('keydown', this.#onKey, { signal });
      this.addEventListener('zoom-media:selected', this.#onZoomSelected, { signal });
      this.addEventListener('pointerenter', () => this.#hydrateAll(), { signal, once: true });

      // Horizon dispatches the variant change on the variant picker; it bubbles through the section.
      const scope = this.closest('.shopify-section') || document;
      scope.addEventListener('shopify:product:select', this.#onProductSelect, { signal });

      this.#paint(this.#index, { instant: true, announce: false, quiet: true });

      const later = () => idle(() => this.#hydrateAll());
      if (document.readyState === 'complete') later();
      else window.addEventListener('load', later, { once: true, signal });
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    get count() {
      return this.#slides().length;
    }

    get index() {
      return this.#index;
    }

    /** @param {number} index */
    select(index) {
      const n = this.count;
      if (!n) return;
      const i = ((Math.trunc(Number(index) || 0) % n) + n) % n;
      this.#paint(i);
    }

    /**
     * @param {string | number} mediaId
     * @param {{ instant?: boolean, announce?: boolean }} [options]
     */
    selectMedia(mediaId, options = {}) {
      const i = this.#slides().findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      this.#paint(i >= 0 ? i : 0, options);
    }

    next() {
      this.select(this.#index + 1);
    }

    prev() {
      this.select(this.#index - 1);
    }

    /** @returns {HTMLElement[]} */
    #slides() {
      return Array.from(this.querySelectorAll('[data-se2-gallery-stage] .se2-gallery__slide'));
    }

    /** @returns {HTMLElement[]} */
    #thumbs() {
      return Array.from(this.querySelectorAll('[data-se2-gallery-thumb]'));
    }

    /** @param {Element | undefined} slide */
    #hydrate(slide) {
      if (!slide) return;
      for (const source of slide.querySelectorAll('source[data-srcset]')) {
        source.setAttribute('srcset', source.getAttribute('data-srcset') || '');
        source.removeAttribute('data-srcset');
      }
    }

    #hydrateAll() {
      for (const slide of this.#slides()) this.#hydrate(slide);
    }

    /**
     * @param {number} i
     * @param {{ instant?: boolean, announce?: boolean, quiet?: boolean }} [options]
     */
    #paint(i, { instant = false, announce = true, quiet = false } = {}) {
      const slides = this.#slides();
      const n = slides.length;
      if (!n) return;
      i = Math.min(n - 1, Math.max(0, i));

      // The photo and its neighbours, so the next arrow press fades into a loaded photo.
      this.#hydrate(slides[i]);
      if (!quiet) {
        this.#hydrate(slides[(i + 1) % n]);
        this.#hydrate(slides[(i - 1 + n) % n]);
      }

      if (instant) this.classList.add('is-instant');

      slides.forEach((slide, k) => {
        const on = k === i;
        slide.classList.toggle('is-active', on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });

      const thumbs = this.#thumbs();
      thumbs.forEach((thumb, k) => {
        const on = k === i;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.#keepThumbInView(thumbs[i]);

      const counter = this.querySelector('[data-se2-gallery-counter]');
      if (counter) counter.textContent = `${pad(i + 1)} / ${n > 99 ? n : pad(n)}`;

      const phrase = (template) =>
        (template || '').replace('[n]', String(i + 1)).replace('[total]', String(n));

      const zoom = this.querySelector('[data-se2-gallery-zoom]');
      if (zoom && this.dataset.tZoom) zoom.setAttribute('aria-label', phrase(this.dataset.tZoom));

      const live = this.querySelector('[data-se2-gallery-live]');
      if (live && announce) live.textContent = phrase(this.dataset.tPhoto || 'Photo [n] of [total]');

      const changed = i !== this.#index;
      this.#index = i;
      this.dataset.active = String(i);

      if (instant) {
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
      }

      if (changed) {
        this.dispatchEvent(
          new CustomEvent('se2:gallery-change', {
            bubbles: true,
            detail: { index: i, mediaId: slides[i]?.dataset.mediaId || '', count: n },
          })
        );
      }
    }

    /** Scrolls the thumbnail strip itself (never the page) so the current thumbnail is in it. */
    #keepThumbInView(thumb) {
      const strip = thumb?.parentElement;
      if (!(thumb instanceof HTMLElement) || !(strip instanceof HTMLElement)) return;
      if (strip.scrollWidth <= strip.clientWidth) return;
      const left = thumb.offsetLeft;
      const right = left + thumb.offsetWidth;
      if (left < strip.scrollLeft) strip.scrollLeft = left - 4;
      else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth + 4;
    }

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const hit = event.target instanceof Element ? event.target.closest('button') : null;
      if (!hit || !this.contains(hit)) return;
      if (hit.hasAttribute('data-se2-gallery-prev')) {
        this.prev();
      } else if (hit.hasAttribute('data-se2-gallery-next')) {
        this.next();
      } else if (hit.hasAttribute('data-se2-gallery-thumb')) {
        this.select(Number(hit.getAttribute('data-se2-gallery-thumb')));
      } else if (hit.hasAttribute('data-se2-gallery-zoom')) {
        this.#openZoom(event);
      }
    };

    /** Left and Right move between photos while focus is on one of the stage's controls. */
    #onKey = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-se2-gallery-stage]')) return;
      event.preventDefault();
      if (event.key === 'ArrowRight') this.next();
      else this.prev();
      // On a thumbnail, focus follows the photo, as in a tab list.
      if (target.hasAttribute('data-se2-gallery-thumb')) this.#thumbs()[this.#index]?.focus();
    };

    /** @param {MouseEvent} event */
    #openZoom(event) {
      const dialog = this.querySelector('zoom-dialog');
      if (!dialog || typeof dialog.open !== 'function') return;
      const id = this.#slides()[this.#index]?.dataset.mediaId || '';
      const items = Array.from(dialog.querySelectorAll('.dialog-zoomed-gallery > li'));
      const at = items.findIndex((li) => li.id.startsWith(`product-${id}-`));
      this.#zoomOpen = true;
      dialog.open(at >= 0 ? at : 0, event);
      const native = dialog.querySelector('dialog');
      native?.addEventListener('close', () => (this.#zoomOpen = false), { once: true });
    }

    /** The zoom dialog says which photo is in view; the stage follows by media id. */
    #onZoomSelected = (event) => {
      if (!this.#zoomOpen) return;
      const index = Number(event.detail?.index);
      const dialog = this.querySelector('zoom-dialog');
      const items = dialog ? Array.from(dialog.querySelectorAll('.dialog-zoomed-gallery > li')) : [];
      const match = items[index]?.id.match(/^product-(\d+)-/);
      if (match) this.selectMedia(match[1], { instant: true, announce: false });
    };

    /** @param {Event & { promise?: Promise<any> }} event */
    #onProductSelect = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog, dialog')) return;
      const promise = event.promise;
      if (!promise || typeof promise.then !== 'function') return;
      const seq = ++this.#seq;

      promise
        .then((result) => {
          if (seq !== this.#seq) return;
          const detail = result?.detail;
          const html = detail?.html;
          const fresh =
            html && typeof html.querySelector === 'function'
              ? html.querySelector(`se2-gallery[data-block-id="${this.dataset.blockId}"]`) || html.querySelector('se2-gallery')
              : null;
          let mediaId = detail?.resource?.featured_media?.id;

          if (fresh instanceof Element) {
            const freshIds = fresh.getAttribute('data-media-ids') || '';
            if (freshIds !== (this.dataset.mediaIds || '')) {
              // Other photos (hide_variants and another colour): the fetched stage replaces this one.
              const stage = this.querySelector('[data-se2-gallery-stage]');
              const next = fresh.querySelector('[data-se2-gallery-stage]');
              if (stage && next) {
                stage.replaceWith(document.importNode(next, true));
                this.dataset.mediaIds = freshIds;
                this.dataset.count = fresh.getAttribute('data-count') || '';
                this.#paint(Number(fresh.getAttribute('data-active')) || 0, { instant: true, announce: false });
                return;
              }
            }
            if (mediaId == null) {
              const ids = freshIds.split(',');
              mediaId = ids[Number(fresh.getAttribute('data-active')) || 0];
            }
          }

          if (mediaId != null) this.selectMedia(mediaId, { announce: false });
          else this.#paint(0, { announce: false });
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[se2-gallery] variant media', error);
        });
    };
  }

  customElements.define('se2-gallery', Se2Gallery);
})();
